/**
 * PULSE SIDE CART — AXEL LAYOUT (2026-08-31)
 *
 * Three controls the reference's shopping bag has and Dawn does not:
 *
 *   <pulse-cart-size>  change a line's size without leaving the bag
 *   <pulse-cart-add>   add another colourway at a chosen size from the bag
 *   <pulse-cart-more>  the "Other colours" band open/closed
 *
 * Replaces assets/pulse-cart-wing.js, which drove the left-hand wing this band
 * took over from. The wing's one durable lesson is carried over verbatim:
 *
 *   Dawn replaces #CartDrawer's innerHTML on every quantity change and after
 *   every add, so these elements are destroyed and rebuilt constantly. State
 *   therefore cannot live on the instance -- it lives in sessionStorage and is
 *   re-read in connectedCallback.
 *
 * Never rebuild the drawer markup by hand. Ask the cart endpoint for the
 * re-rendered sections and hand the whole response to cart-drawer.renderContents,
 * which also repaints the cart bubble and re-binds the overlay close handler;
 * assets/pulse-nav.js then restores this design's header markup after it.
 */

const MORE_KEY = 'pulse-cart-more';

/** The sections cart-drawer.js knows how to repaint, as the cart API wants them. */
function sectionsParam() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.getSectionsToRender !== 'function') return null;
  return drawer.getSectionsToRender()
    .map((section) => section.id)
    .join(',');
}

async function postCart(path, payload) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  // Parse defensively. A rate limit or the storefront's "Verifying your
  // connection" interstitial answers with HTML, and response.json() threw a
  // SyntaxError that reached the customer verbatim (audit JS-5). A non-JSON
  // body is the theme's generic cart error; a 422 keeps Shopify's own sentence
  // in `description` ("...is already sold out"); `message` is the short form.
  const text = await response.text();
  let data = null;
  try {
    data = JSON.parse(text);
  } catch (error) {
    data = null;
  }
  if (!response.ok || data === null) {
    throw new Error((data && (data.description || data.message)) || genericCartError());
  }
  return data;
}

function genericCartError() {
  return (window.cartStrings && window.cartStrings.error) || 'That did not work. Please try again.';
}

/**
 * Repaint the drawer from a cart response that carried `sections`.
 *
 * renderContents OPENS the cart as a side effect, and the saved-items panel is
 * a full-height right-hand drawer at the same stacking level -- so adding to
 * the bag FROM the saved panel would leave two drawers stacked, the saved one
 * painting over the cart it just filled. Closing it here rather than in the
 * saved panel keeps the rule with the thing that causes it: whatever opens the
 * cart owns getting the other panel out of the way.
 */
function repaint(cart) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || !cart || !cart.sections) return;
  const saved = document.querySelector('pulse-saved-drawer.active');
  if (saved && typeof saved.close === 'function') saved.close();
  drawer.renderContents(cart);
  applyScale();
}

/* ---------------------------------------------------------------------------
   WOMEN'S / MEN'S SIZE SCALE

   The cart used to print the men's half of "US M 4 / US W 5 / EU 35.5" whatever
   the customer was browsing, so a women's 7.5 arrived in the bag as a men's 6.5
   (Andre, 2026-08-31). The variant was always right; only the label lied.

   Everywhere else on the site both readings are in the DOM and CSS picks one.
   That cannot work inside a <select>: an <option> has no styleable children.
   assets/pulse-product.js already solved this on the PDP by rewriting the
   option text from data-text-w / data-text-m, and this is the same move. The
   option's VALUE is never touched, which is what keeps the chosen size valid
   across a switch.

   The preference itself belongs to window.PulseSizeScale
   (snippets/pulse-size-scale.liquid) -- one key for the PDP, the wall and here.
--------------------------------------------------------------------------- */

/** The size in words for the option in play, for the visible `Size` property. */
function sizeWords(select, scale) {
  const opt = select && select.options[select.selectedIndex];
  return opt ? opt.getAttribute(scale === 'm' ? 'data-label-m' : 'data-label-w') : null;
}

/**
 * Rewrite every size label to the scale in force -- EXCEPT where a line pins its
 * own. A cart line carries `data-scale-lock` when it was added with a `_scale`
 * property, because the size you chose is part of what you bought: a bag with a
 * men's 11 and a women's 10 in it must show both, not relabel the pair you
 * already picked when you switch scales to pick the next one.
 *
 * Rows that are NOT cart lines -- Other colours, the saved panel -- carry no
 * lock and follow the global preference, which is right: nothing has been
 * chosen there yet.
 */
function applyScale(root) {
  const fallback = window.PulseSizeScale.get();
  (root || document).querySelectorAll('[data-text-w][data-text-m]').forEach((el) => {
    const holder = el.closest('[data-scale-lock]');
    const scale = (holder && holder.getAttribute('data-scale-lock')) || fallback;
    const text = el.getAttribute(scale === 'm' ? 'data-text-m' : 'data-text-w');
    if (text) el.textContent = text;
  });
}

function showError(message) {
  const box = document.getElementById('CartDrawer-CartErrors');
  if (box) box.textContent = message;
}

/**
 * SIZE ON A LINE.
 *
 * Shopify has no endpoint that swaps a line's variant, so this is two calls,
 * and the ORDER is the whole safety argument:
 *
 *   1. add the new size
 *   2. only then remove the old line
 *
 * Reversed, a sold-out target would leave the customer with nothing. Removal is
 * by line `key`, not by index, because step 1 re-orders the cart and an index
 * would then point at the wrong pair.
 */
class PulseCartSize extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-size-select]');
    if (!this.select) return;
    // Dawn rebuilds this element on every quantity change and every add, so
    // connectedCallback is the one hook guaranteed to run against fresh markup.
    applyScale(this);
    this.previous = this.select.value;
    this.select.addEventListener('change', this.onChange.bind(this));
  }

  async onChange() {
    const variantId = this.select.value;
    if (!variantId || variantId === this.previous) return;

    const key = this.dataset.lineKey;
    const quantity = parseInt(this.dataset.quantity, 10) || 1;
    const sections = sectionsParam();

    this.classList.add('is-busy');
    this.select.disabled = true;
    showError('');

    let added = false;
    try {
      // The replacement inherits the line's own scale, so swapping size does not
      // quietly re-label the pair in whatever scale is being browsed right now.
      const lock = this.dataset.scaleLock || window.PulseSizeScale.get();
      const swapProps = { _scale: lock };
      const swapWords = sizeWords(this.select, lock);
      if (swapWords) swapProps.Size = swapWords;
      const swapItem = { id: Number(variantId), quantity, properties: swapProps };
      const swapPlan = planOf(this.select);
      if (swapPlan) swapItem.selling_plan = swapPlan;
      await postCart('/cart/add.js', {
        items: [swapItem],
      });
      added = true;
      const cart = await postCart('/cart/change.js', {
        id: key,
        quantity: 0,
        sections,
        sections_url: window.location.pathname,
      });
      repaint(cart);
      // No cleanup after repaint: this element no longer exists.
    } catch (error) {
      if (!added) {
        // The add failed, so the bag is untouched and the select is the only
        // thing out of step with it. Put it back.
        this.select.value = this.previous;
        this.select.disabled = false;
        this.classList.remove('is-busy');
        showError(error.message);
        return;
      }
      // The new size IS in the bag but the old line could not be removed, so
      // the drawer on screen is now a lie: it shows one line while the cart
      // holds two (audit JS-6). Repaint from the cart itself (an empty update
      // is a no-op that still returns the rendered sections), then say what
      // happened. showError runs AFTER repaint because the alert region is
      // inside the markup renderContents replaces.
      let cart = null;
      try {
        cart = await postCart('/cart/update.js', {
          updates: {},
          sections,
          sections_url: window.location.pathname,
        });
      } catch (refreshError) {
        cart = null;
      }
      if (cart) {
        repaint(cart);
      } else {
        this.select.disabled = false;
        this.classList.remove('is-busy');
      }
      showError('The new size is in your bag, but the old size could not be removed. Please remove it below.');
    }
  }
}

customElements.define('pulse-cart-size', PulseCartSize);

/** The selling plan on the chosen <option> (data-plan, a pre-order size in this
 *  market), as a number for /cart/add.js, or null for a stocked size. */
function planOf(select) {
  const opt = select && select.options[select.selectedIndex];
  const plan = opt && opt.getAttribute('data-plan');
  return plan ? Number(plan) : null;
}

/** ADD ANOTHER COLOURWAY, at a size, from inside the bag. */
class PulseCartAdd extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-add-select]');
    this.button = this.querySelector('[data-add-button]');
    if (!this.select || !this.button) return;
    applyScale(this);
    this.button.addEventListener('click', this.onAdd.bind(this));
  }

  async onAdd() {
    const variantId = this.select.value;
    if (!variantId) {
      // Nothing chosen yet: send them to the control rather than to an error.
      this.select.focus();
      this.classList.add('is-asking');
      return;
    }

    this.classList.remove('is-asking');
    this.classList.add('is-busy');
    this.button.disabled = true;
    showError('');

    try {
      const scale = window.PulseSizeScale.get();
      const addProps = { _scale: scale };
      const addWords = sizeWords(this.select, scale);
      if (addWords) addProps.Size = addWords;
      const addItem = { id: Number(variantId), quantity: 1, properties: addProps };
      const addPlan = planOf(this.select);
      if (addPlan) addItem.selling_plan = addPlan;
      const cart = await postCart('/cart/add.js', {
        items: [addItem],
        sections: sectionsParam(),
        sections_url: window.location.pathname,
      });
      repaint(cart);
    } catch (error) {
      this.button.disabled = false;
      this.classList.remove('is-busy');
      showError(error.message);
    }
  }
}

customElements.define('pulse-cart-add', PulseCartAdd);

/**
 * THE "OTHER COLOURS" BAND.
 *
 * Open by default, because a cross-sell nobody opens is not a cross-sell. The
 * choice is remembered for the session so a customer who shut it does not have
 * it thrown back at them on the next add -- which is exactly when Dawn rebuilds
 * this element from scratch.
 */
class PulseCartMore extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-more-toggle]');
    if (!this.toggle) return;
    this.toggle.addEventListener('click', () => this.set(!this.classList.contains('is-open'), true));

    let stored = null;
    try {
      stored = sessionStorage.getItem(MORE_KEY);
    } catch (error) {
      /* private mode: the band just always opens, which is survivable */
    }

    // Synchronously, not on the next frame. The wing learned this the hard way:
    // Dawn re-renders #CartDrawer's innerHTML on load, so a frame callback fires
    // against an element that has already been replaced and the state is lost.
    this.set(stored === null ? true : stored === 'open', false);
    requestAnimationFrame(() => this.classList.add('is-ready'));
  }

  set(open, remember) {
    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    if (!remember) return;
    try {
      sessionStorage.setItem(MORE_KEY, open ? 'open' : 'closed');
    } catch (error) {
      /* private mode: the band just forgets */
    }
  }
}

customElements.define('pulse-cart-more', PulseCartMore);

/* The scale can change while the cart is open: the wall's switch sits behind
   the scrim and the PDP's toggle is on the page under it. Both go through the
   shared module now, so one subscription replaces the attribute observer and
   the click sniffing this used to need. */
(function watchScale() {
  const run = () => applyScale();
  window.PulseSizeScale.onChange(run);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
